import { Button, Form, Input, Select, Space } from "antd";
import TextArea from "antd/es/input/TextArea";
import { useEffect } from "react";
import CustomModal from "../../../../../components/ui/CustomModal";
import {
  useCreateFeatureMutation,
  useUpdateFeatureMutation,
} from "../../../../../features/superAdmin/ticketService/feature/feature.api";
import { useGetAllProductsQuery } from "../../../../../features/superAdmin/ticketService/product/product.api";
import useError from "../../../../../hooks/useError";
import { toast } from "../../../../../lib/toast";
import { CustomModalProps } from "../../../../../types";

type Props = { data: any } & CustomModalProps;

const ManageFeature = ({ open, handleClose, data, ...rest }: Props) => {
  const [form] = Form.useForm();
  const isUpdating = !!data?.id;

  // ------ Queries ------
  const { data: products } = useGetAllProductsQuery({
    params: { page: 1, limit: 1000000, status: "active" },
  });
  const productOptions = products?.data?.products?.map((p) => ({
    value: p.id,
    label: p.title,
  }));

  // ------ Mutations ------
  const [
    createProduct,
    {
      isLoading: creating,
      isError: isCreateError,
      error: createError,
      isSuccess: created,
    },
  ] = useCreateFeatureMutation();
  const [
    updateProduct,
    {
      isLoading: updating,
      isError: isUpdateError,
      error: updateError,
      isSuccess: updated,
    },
  ] = useUpdateFeatureMutation();

  // ------ Function for handle form finish ------
  const handleFinish = (values: any) => {
    if (isUpdating) {
      updateProduct({
        id: data?.id,
        data: values,
      });
    } else {
      createProduct(values);
    }
  };

  // ------ Effects ------
  useEffect(() => {
    if (isUpdating) {
      form.setFieldsValue(data);
    }
  }, [data, isUpdating, form]);

  useEffect(() => {
    if ((created || updated) && !(creating || updating)) {
      handleClose();
      form.resetFields();
      toast(
        "success",
        isUpdating
          ? "Feature updated successfully."
          : "Feature created successfully.",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [created, updated, creating, updating]);

  // ------ Errors ------
  useError(isCreateError, createError, "Feature create fail.");
  useError(isUpdateError, updateError, "Feature update fail.");

  return (
    <CustomModal
      {...rest}
      width={400}
      open={open}
      handleClose={() => {
        handleClose();
        form.resetFields();
      }}
      title={isUpdating ? "Update Feature" : "Add New Feature"}
      footer={null}
    >
      <Form
        requiredMark={false}
        form={form}
        onFinish={handleFinish}
        layout="vertical"
      >
        <Form.Item
          rules={[{ required: true, message: "Please enter feature title" }]}
          name="title"
          label="Feature Title"
          className="mb-4"
        >
          <Input size="large" placeholder="Enter feature title" />
        </Form.Item>
        <Form.Item
          rules={[{ required: true, message: "Please select a product" }]}
          name="product_id"
          label="Select Product"
          className="mb-4"
        >
          <Select
            options={productOptions}
            size="large"
            placeholder="Select Product"
          />
        </Form.Item>
        <Form.Item
          name="description"
          label="Feature Description"
          className="mb-4"
        >
          <TextArea size="large" placeholder="Enter feature description" />
        </Form.Item>

        <Space className="flex justify-end items-center">
          <Button
            onClick={handleClose}
            htmlType="button"
            className="button-bordered-light"
          >
            Cancel
          </Button>
          <Button htmlType="submit" className="button-primary-light">
            {isUpdating ? "Update" : "Add"}
          </Button>
        </Space>
      </Form>
    </CustomModal>
  );
};

export default ManageFeature;
